/**
 * P4-R2C-003 §5 — the Profit Path draft-sync decisions, as production
 * functions rather than inline expressions.
 *
 * WHY THEY WERE EXTRACTED. The Profit Path call sites were covered only by
 * structural AST tests, and two negative controls survived them: passing
 * `notify: false` at the consent site (restoring a silent failure) and
 * writing the booking guard as `bookingResult.ok || true`. Both still mention
 * `.ok` and both still look like a versioned call, so no source-shape
 * assertion can tell them apart from the correct code.
 *
 * Moving the two decisions here makes them executable, so a test can assert
 * what they DO. A control now has to either change a function -- which the
 * behavioural tests catch -- or bypass it, which the AST test that every call
 * site uses these helpers catches. There is no longer a shape that defeats
 * both.
 */

/** The label each draft-sync site reports a failure under. */
export const DRAFT_SYNC_LABELS = Object.freeze({
  theme: "Funnel theme",
  consent: "Consent language",
  booking: "Booking configuration",
  appearance: "Funnel appearance",
});

/**
 * Options for a draft-sync write.
 *
 * `notify` is ALWAYS true. Every one of these four sites previously swallowed
 * its failure -- consent language simply stopped reaching the funnel with
 * nothing shown to the operator -- so suppressing notification is not a
 * choice a call site gets to make. It is returned as a value (rather than
 * omitted) so a test can assert it, and so a site that wanted to suppress it
 * would have to stop using this function and fail the AST check.
 */
export function draftSyncOptions(kind) {
  const label = DRAFT_SYNC_LABELS[kind];
  if (!label) {
    throw new Error(`unknown draft-sync kind: ${String(kind)}`);
  }
  return { label, notify: true };
}

/**
 * Whether a booking patch may be recorded as applied.
 *
 * `booking_calendar_patched` is read by publish-readiness, so recording it
 * after a rejected save persists a claim that is not true. It requires BOTH a
 * calendar to patch and a COMMITTED write -- `result.ok` must be exactly
 * true, so a truthy-but-not-committed result cannot slip through.
 */
export function shouldMarkBookingPatched(patchCalendarId, result) {
  return Boolean(patchCalendarId) && result?.ok === true;
}
